import { useEffect, useState } from 'react';
import { useOrganization, useOrganizationList, useSession, useUser, useClerk } from '@clerk/clerk-react';
import * as Sentry from '@sentry/react';
import { useLocation } from 'react-router-dom';

import { switchToOrganization, setClerkInstance } from '@services/auth';

const useClerkLoginState = () => {
	const [state, setState] = useState('');

	const { membership } = useOrganization();
	const { session } = useSession();
	const { isLoaded: isOrganisationListLoaded } = useOrganizationList();
	const { user, isLoaded: isUserLoaded } = useUser();
	const clerk = useClerk();
	setClerkInstance(clerk);

	// Allows switching orgs by passing a query param: `?org={org-id}`
	const { search, pathname } = useLocation();
	const targetOrg = search.split('org=')[1]?.split('&')[0];

	useEffect(() => {
		if (!session || !isUserLoaded || !isOrganisationListLoaded) return;
		if (state === 'loading') return;

		if (membership && (!targetOrg || membership?.organization.id === targetOrg)) {
			// we already have an active organization - all good
			setState('logged-in');
			return;
		}

		const organisations = user.organizationMemberships;
		if (organisations.length === 0) {
			if (user.unsafeMetadata?.product === 'unify') {
				// A user who is mid-onboarding legitimately has 0 orgs for a brief
				// window — the onboarding page is creating their org right now. Don't
				// eject them to the canonical dashboard host (which, on any host that
				// isn't staging/preview — i.e. localhost, sandbox, or production-less
				// hosts — wrongly defaults to the production domain, breaking the
				// fresh sign-up + auto-login flow from NEA-5767).
				if (pathname.includes('onboarding')) return;
				const host = window.location.hostname;
				const base = host.includes('staging')
					? 'https://staging.app.unify.near.st'
					: host.includes('preview')
						? 'https://preview.app.unify.near.st'
						: 'https://app.unify.near.st';
				window.location.replace(base);
				return;
			}
			setState('no-organisation');
			Sentry.captureException(new Error('User does not have a relationship with an organisation.'));
			return;
		}

		(async () => {
			setState('loading');
			await switchToOrganization(targetOrg || organisations[0].organization.id);
			setState('logged-in');
		})();
	}, [session, isUserLoaded, isOrganisationListLoaded, membership, targetOrg, pathname]);

	return session && state;
};

export default useClerkLoginState;
